import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Button, Alert } from '@mui/material';
import { Print, ArrowBack, PictureAsPdf, Edit } from '@mui/icons-material';
import html2pdf from 'html2pdf.js';
import { invoiceService } from '../../services/invoiceService';
import { formatCurrency, formatIndianDate, numberToWords } from '../../utils/currency';
import '../../styles/invoice-print.css';

interface CompanySettings {
  companyName: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  email: string;
  gstin: string;
  bankName: string;
  bankAccountNumber: string;
  bankIfscCode: string;
  bankBranch: string;
}

interface InvoiceWithDetails {
  id: number;
  invoiceNumber: string;
  customerId: number;
  salesOrderId?: number;
  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
  totalAmount: number;
  paidAmount: number;
  status: string;
  // Customer details from API
  companyName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  customerGstin?: string;
  orderNumber?: string;
  // Items with all fields optional for flexibility
  items?: {
    id?: number;
    productId: number;
    productName?: string;
    hsnCode?: string;
    unit?: string;
    quantity: number;
    unitPrice: number;
    discountPercent?: number;
    cgstAmount?: number;
    sgstAmount?: number;
    igstAmount?: number;
    lineTotal?: number;
  }[];
}

const InvoicePrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [invoice, setInvoice] = useState<InvoiceWithDetails | null>(null);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('Invoice ID is required');
        setLoading(false);
        return;
      }

      const invoiceId = parseInt(id, 10);
      if (isNaN(invoiceId)) {
        setError('Invalid Invoice ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Load invoice and company settings in parallel
        const [invoiceData, settingsData] = await Promise.all([
          invoiceService.getInvoiceById(invoiceId),
          invoiceService.getCompanySettings()
        ]);

        setInvoice(invoiceData);
        setCompanySettings(settingsData);
      } catch (err) {
        console.error('Error loading invoice data:', err);
        setError('Failed to load invoice. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    if (!invoiceRef.current || !invoice) return;

    setDownloading(true);
    try {
      const element = invoiceRef.current;
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `${invoice.invoiceNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt as any).from(element).save();
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleBack = () => {
    navigate('/sales/invoices');
  };

  // Determine if invoice is intra-state or inter-state based on GST
  const isInterState = invoice && companySettings &&
    invoice.igstAmount && invoice.igstAmount > 0;

  // Calculate taxable amount per item
  const calculateTaxableAmount = (item: any) => {
    const lineAmount = item.quantity * item.unitPrice;
    const discount = (lineAmount * (item.discountPercent || 0)) / 100;
    return lineAmount - discount;
  };

  if (loading) {
    return (
      <div className="invoice-print-wrapper">
        <div className="invoice-loading">
          <CircularProgress className="invoice-loading-spinner" />
          <div>Loading invoice...</div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="invoice-print-wrapper">
        <div className="invoice-print-container">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Invoice not found'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            Back to Invoices
          </Button>
        </div>
      </div>
    );
  }

  const settings = companySettings || {
    companyName: 'Your Company Name',
    address: 'Company Address',
    city: 'City',
    state: 'State',
    pincode: '000000',
    phone: '+91 XXXXXXXXXX',
    email: 'email@company.com',
    gstin: 'XXAXXXXXX1XXXXX',
    bankName: 'Bank Name',
    bankAccountNumber: 'XXXXXXXXXXXX',
    bankIfscCode: 'XXXXXXX',
    bankBranch: 'Branch Name'
  };

  return (
    <div className="invoice-print-wrapper">
      {/* Print Actions - Hidden during print */}
      <div className="print-actions no-print">
        <Button
          variant="contained"
          className="back-btn"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ bgcolor: '#757575', '&:hover': { bgcolor: '#616161' } }}
        >
          Back
        </Button>
        {invoice.status === 'DRAFT' && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/sales/invoices/new?editId=${id}`)}
            sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d5f58' } }}
          >
            Edit Invoice
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
          onClick={handleDownloadPdf}
          disabled={downloading}
          sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
        >
          {downloading ? 'Generating...' : 'Download PDF'}
        </Button>
        <Button
          variant="contained"
          className="print-btn"
          startIcon={<Print />}
          onClick={handlePrint}
          sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
        >
          Print Invoice
        </Button>
      </div>

      <div className="invoice-print-container" ref={invoiceRef}>
        {/* Invoice Header */}
        <div className="invoice-header">
          <div className="invoice-title">Tax Invoice</div>
          <div className="company-name">{settings.companyName}</div>
          <div className="company-address">
            {settings.address}, {settings.city}, {settings.state} - {settings.pincode}
          </div>
          <div className="company-address">
            Phone: {settings.phone} | Email: {settings.email}
          </div>
          <div className="company-gstin">GSTIN: {settings.gstin}</div>
        </div>

        {/* Invoice Details Row */}
        <div className="invoice-details-row">
          <div className="invoice-details-col">
            <div>
              <span className="detail-label">Invoice No.</span>
              <div className="detail-value">{invoice.invoiceNumber}</div>
            </div>
            <div style={{ marginTop: '10px' }}>
              <span className="detail-label">Order Ref</span>
              <div className="detail-value">{invoice.orderNumber || '-'}</div>
            </div>
          </div>
          <div className="invoice-details-col">
            <div>
              <span className="detail-label">Invoice Date</span>
              <div className="detail-value">
                {formatIndianDate(new Date(invoice.invoiceDate))}
              </div>
            </div>
            <div style={{ marginTop: '10px' }}>
              <span className="detail-label">Due Date</span>
              <div className="detail-value">
                {formatIndianDate(new Date(invoice.dueDate))}
              </div>
            </div>
          </div>
        </div>

        {/* Bill To / Ship To */}
        <div className="party-details-row">
          <div className="party-box">
            <div className="party-title">Bill To</div>
            <div className="party-name">{invoice.companyName}</div>
            <div className="party-address">{invoice.address}</div>
            <div className="party-address">
              {invoice.city}, {invoice.state} - {invoice.pincode}
            </div>
            {invoice.customerGstin && (
              <div className="party-gstin">GSTIN: {invoice.customerGstin}</div>
            )}
          </div>
          <div className="party-box">
            <div className="party-title">Ship To</div>
            <div className="party-name">{invoice.companyName}</div>
            <div className="party-address">{invoice.address}</div>
            <div className="party-address">
              {invoice.city}, {invoice.state} - {invoice.pincode}
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <table className="invoice-table">
          <thead>
            <tr>
              <th style={{ width: '30px' }}>#</th>
              <th className="item-description">Description</th>
              <th style={{ width: '70px' }}>HSN</th>
              <th className="text-center" style={{ width: '50px' }}>Qty</th>
              <th className="text-right" style={{ width: '80px' }}>Rate</th>
              <th className="text-center" style={{ width: '50px' }}>Disc%</th>
              <th className="text-right" style={{ width: '100px' }}>Taxable Amt</th>
              {!isInterState ? (
                <>
                  <th className="text-right" style={{ width: '80px' }}>CGST</th>
                  <th className="text-right" style={{ width: '80px' }}>SGST</th>
                </>
              ) : (
                <th className="text-right" style={{ width: '80px' }}>IGST</th>
              )}
              <th className="text-right" style={{ width: '100px' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items?.map((item, index) => {
              const taxableAmount = calculateTaxableAmount(item);
              return (
                <tr key={item.id || index}>
                  <td className="text-center">{index + 1}</td>
                  <td className="item-description">{item.productName}</td>
                  <td className="item-hsn">{item.hsnCode || '-'}</td>
                  <td className="text-center">
                    {item.quantity} {item.unit || ''}
                  </td>
                  <td className="text-right amount">{formatCurrency(item.unitPrice)}</td>
                  <td className="text-center">{item.discountPercent || 0}%</td>
                  <td className="text-right amount">{formatCurrency(taxableAmount)}</td>
                  {!isInterState ? (
                    <>
                      <td className="text-right amount">{formatCurrency(item.cgstAmount ?? 0)}</td>
                      <td className="text-right amount">{formatCurrency(item.sgstAmount ?? 0)}</td>
                    </>
                  ) : (
                    <td className="text-right amount">{formatCurrency(item.igstAmount ?? 0)}</td>
                  )}
                  <td className="text-right amount">{formatCurrency(item.lineTotal ?? 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals Section */}
        <div className="totals-section">
          <table className="totals-table">
            <tbody>
              <tr>
                <td className="label">Subtotal</td>
                <td className="value">{formatCurrency(invoice.subtotal)}</td>
              </tr>
              {!isInterState ? (
                <>
                  <tr>
                    <td className="label">CGST</td>
                    <td className="value">{formatCurrency(invoice.cgstAmount)}</td>
                  </tr>
                  <tr>
                    <td className="label">SGST</td>
                    <td className="value">{formatCurrency(invoice.sgstAmount)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td className="label">IGST</td>
                  <td className="value">{formatCurrency(invoice.igstAmount)}</td>
                </tr>
              )}
              <tr className="grand-total">
                <td className="label">Total Amount</td>
                <td className="value">{formatCurrency(invoice.totalAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Amount in Words */}
        <div className="amount-in-words">
          <div className="amount-in-words-label">Amount in Words</div>
          <div className="amount-in-words-value">
            {numberToWords(invoice.totalAmount)}
          </div>
        </div>

        {/* Bank Details & Terms */}
        <div className="footer-section">
          <div className="bank-details">
            <div className="section-title">Bank Details</div>
            <div className="bank-detail-row">
              <span className="bank-label">Bank Name:</span>
              <span className="bank-value">{settings.bankName}</span>
            </div>
            <div className="bank-detail-row">
              <span className="bank-label">Account No:</span>
              <span className="bank-value">{settings.bankAccountNumber}</span>
            </div>
            <div className="bank-detail-row">
              <span className="bank-label">IFSC Code:</span>
              <span className="bank-value">{settings.bankIfscCode}</span>
            </div>
            <div className="bank-detail-row">
              <span className="bank-label">Branch:</span>
              <span className="bank-value">{settings.bankBranch}</span>
            </div>
          </div>
          <div className="terms-section">
            <div className="section-title">Terms & Conditions</div>
            <ul className="terms-list">
              <li>Payment is due within the specified due date.</li>
              <li>Interest @ 18% p.a. will be charged on delayed payments.</li>
              <li>All disputes are subject to local jurisdiction.</li>
              <li>Goods once sold will not be taken back or exchanged.</li>
              <li>E. & O.E. (Errors and Omissions Excepted)</li>
            </ul>
          </div>
        </div>

        {/* Signature Section */}
        <div className="signature-section">
          <div className="signature-box">
            <div className="signature-line"></div>
            <div className="signature-label">Authorised Signatory</div>
            <div className="company-stamp">For {settings.companyName}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicePrintPage;
